import React from "react";
import "../DashboardStyles.css";
import PanhandleMap from "./PanhandleMap";

const COUNCIL = [
  "Valerie Beel",
  "Lisa Hutchison",
  "Jordan Diedrich",
  "Dr. Andrew Dick",
  "Misty Curtis",
  "Scott Yates",
  "Dr. Derek Peil",
];

const STAFF = [
  "Bryan Venable, Executive Director",
  "David Welanko, Grant Manager",
  "Kyle Keller, Community Development Specialist",
];

const BOARD = [
  { name: "Janine Schmidt",   position: "Morrill Clerk",                    office: "Board President" },
  { name: "Jenni Mattern",    position: "Terrytown Clerk",                  office: "Board Vice President" },
  { name: "Pat Comfort",      position: "At large member",                  office: "Board Secretary/Treasurer" },
  { name: "Kevin Spencer",    position: "Scottsbluff City Manager" },
  { name: "Pat Heath",        position: "Gering City Manager" },
  { name: "Josh Hanson",      position: "Sidney City Manager" },
  { name: "John Reed",        position: "NNDC Executive Director" },
  { name: "Philip Sanders",   position: "Cheyenne Co. Commissioner" },
  { name: "Annette Brower",   position: "Kimball City Manager" },
  { name: "Walter Juhlin",    position: "Dawes County Commissioner" },
  { name: "Savanna Daly",     position: "Mitchell City Manager" },
  { name: "Scott Fleming",    position: "Gordon City Manager" },
  { name: "Gail Beyer",       position: "Bridgeport Mayor" },
  { name: "Shelly Bowlin",    position: "Bayard City Clerk" },
  { name: "Michael Minzey",   position: "Terrytown board member" },
  { name: "Joshua Skavdahl",  position: "Sioux County Commissioner" },
  { name: "Brian Frahm",      position: "Crawford Council Member" },
  { name: "James Bondergar",  position: "Oshkosh City Administrator/Clerk" },
  { name: "Don Lease",        position: "Banner County Commissioner" },
  { name: "Elyse Lukassen",   position: "Kimball County Commissioner" },
  { name: "Kim Robb",         position: "Lyman Clerk" },
  { name: "Kendra Mitchell",  position: "Potter Council Member" },
];

export default function Chapter1Dashboard() {
  return (
    <div className="chapter chapter1">
      <div className="report-title-block">
        <h1>Comprehensive Economic Development Strategy</h1>
        <p>
          <strong>2026–2031 • Five-Year Regional Strategy</strong>
        </p>
        <p>
          Prepared for the eleven-county Nebraska Panhandle:<br />
          Banner • Box Butte • Cheyenne • Dawes • Deuel • Garden • Kimball •
          Morrill • Scotts Bluff • Sheridan • Sioux
        </p>
        <p>
          <strong>Panhandle Area Development District (PADD)</strong>
        </p>

        <PanhandleMap />
      </div>

      <div className="acknowledgements-block">
        <h2>Acknowledgements</h2>
        <p>
          The Panhandle Area Development District gratefully acknowledges the
          contributions of the CEDS Strategy Council, regional stakeholders,
          partner organizations, and the community members across the
          eleven-county Nebraska Panhandle who informed this strategy, including
          the 203 residents who completed the community survey and the 3,826
          students who shared their plans through the Western Nebraska Teen
          Opportunities Survey.
        </p>

        <h4>CEDS Strategy Council</h4>
        <ul className="people-list">
          {COUNCIL.map((n) => <li key={n}>{n}</li>)}
        </ul>

        <h4>PADD Staff</h4>
        <ul className="people-list">
          {STAFF.map((n) => <li key={n}>{n}</li>)}
        </ul>

        <h4>PADD Board of Directors</h4>
        <div className="table-wrapper">
          <table className="ceds-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Position</th>
                <th scope="col">Board Office</th>
              </tr>
            </thead>
            <tbody>
              {BOARD.map((b) => (
                <tr key={b.name}>
                  <td>{b.name}</td>
                  <td>{b.position}</td>
                  <td>{b.office || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="chapter-content">
        <p className="chapter-eyebrow">Chapter 1</p>
        <h2>Executive Summary</h2>

        <h3>A High-Impact, Accountable, and Citizen-Facing CEDS</h3>
        <p>
          The Panhandle Area Development District’s five-year Comprehensive
          Economic Development Strategy (CEDS) is designed to translate regional
          vision into action through a living, citizen-facing framework.
          Grounded in the region’s historical planning foundations, the document
          reflects lessons drawn from a review of high-performing and regionally
          comparable CEDS from across the country, particularly those
          confronting challenges similar to those in the Nebraska Panhandle.
          Building on previous local efforts, this version pairs regional goals
          with measurable outcomes, clearly defined responsibility, and regular
          review so progress remains visible and trackable.
        </p>

        <div className="callout">
          <span className="callout-label">How this plan was built</span>
          <p>
            This CEDS is grounded in primary community input: a{" "}
            <strong>203-response community survey</strong>, the{" "}
            <strong>3,826-student Western Nebraska Teen Opportunities Survey</strong>,
            and the <strong>CEDS Strategy Council</strong>, alongside federal and
            state data. Chapter 11 describes that engagement in full, including
            an account of the planning climate in which this cycle was produced.
            Every goal that follows traces back to what the region’s people said
            they needed.
          </p>
        </div>

        <h3>Rationale for the Updated Framework</h3>
        <p>The 2026–2031 CEDS was developed with three guiding principles:</p>

        <h4>1. Focus on Measurable Outcomes, Not Activities</h4>
        <p>
          The plan prioritizes net new jobs, capital investment, wage growth,
          housing production, and workforce development outcomes rather than
          simply listing services or programs.
        </p>

        <h4>2. Align Strategy with Regional Reality</h4>
        <p>
          The Panhandle’s economic future depends on strengthening existing
          industry clusters, supporting entrepreneurship and second-stage firms,
          expanding workforce participation, addressing housing and
          infrastructure constraints, and coordinating region-wide collaboration.
        </p>

        <h4>3. Create Accountability Without Creating Division</h4>
        <p>
          Performance measures are structured at the regional level, emphasizing
          growth, leverage, and collaboration while avoiding unnecessary
          community-to-community comparisons.
        </p>

        <h3>Strategic Structure</h3>
        <p>The CEDS is organized around four integrated goals:</p>
        <ul className="ceds-list">
          <li>
            <strong>Goal 1: Strengthen the Foundations People and Businesses
            Need to Stay</strong>: Addressing the interlocking barriers of
            housing shortage and childcare access that constrain workforce
            retention across the region.
          </li>
          <li>
            <strong>Goal 2: Grow and Keep a Skilled Regional Workforce</strong>:
            Accelerating business succession planning and building
            employer-linked workforce pipelines in high-demand sectors.
          </li>
          <li>
            <strong>Goal 3: Build and Modernize the Infrastructure Communities
            Depend On</strong>: Systematically advancing water, wastewater, and
            broadband infrastructure investments through a disciplined,
            prioritized project pipeline.
          </li>
          <li>
            <strong>Goal 4: Build Regional Capacity, Accountability &amp;
            Long-Term Economic Resilience</strong>: Strengthening regional
            coordination, public accountability and visibility, natural-resource
            stewardship, and the economic diversification strategies needed to
            sustain the Panhandle through the inevitable cycles of change.
          </li>
        </ul>

        <h3>Quantifiable Performance Framework</h3>
        <p>
          Each objective includes clear Key Performance Indicators (KPIs) with
          baseline measurements, five-year targets, and annual review
          requirements. Core performance categories include net new jobs in
          target sectors, private capital investment leveraged, business
          formations and expansions, workforce training completions, housing
          units developed or rehabilitated, grant dollars secured region-wide,
          and multi-jurisdictional initiatives implemented. Progress will be
          reviewed annually by the CEDS Strategy Council to ensure alignment
          with emerging opportunities and economic conditions.
        </p>

        <h3>A Living, Citizen-Facing Document</h3>
        <p>
          This CEDS is designed to inform citizens how regional economic
          development efforts translate into tangible outcomes, provide elected
          officials with measurable benchmarks, align public and private
          partners under shared priorities, strengthen eligibility for federal
          and state funding, and maintain flexibility while preserving
          accountability. Rather than serving as a one-time publication, this
          CEDS will function as an ongoing strategic dashboard guiding
          investment decisions, partnership development, and policy alignment.
        </p>

        <h3>Conclusion</h3>
        <p>
          The updated CEDS positions the Nebraska Panhandle to move from
          aspirational planning to measurable impact. By focusing on outcomes,
          aligning regional strengths, and instituting disciplined performance
          review, this strategy establishes a high-impact, sustainable economic
          development framework capable of adapting to change while maintaining
          long-term direction.
        </p>
      </div>
    </div>
  );
}
